import _ from "lodash";

const DN_ONLY_FIELDS = ["dnStatus", "dnPaymentStatus", "dnPolicyStatus"];
const ORDER_ONLY_FIELDS = [
  "orderStatus",
  "policyCommenceDate",
  "policyAckSignDate",
];

const PRESENCE_VALUES = {
  MISSING: "MISSING",
  SET: "SET",
};

export const BY_ORDER_FILTER_COLUMNS = [
  {
    field: "productCategory",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "productCategory",
      label: "Product Category",
      width: 250,
      height: 300,
      refField: "productCategory",
    },
  },
  {
    field: "productType",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "productType",
      label: "Product Type",
      width: 350,
      height: 300,
      refField: "productType",
    },
  },
  {
    field: "provider",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "provider",
      label: "Insurer",
      width: 320,
      height: 300,
      refField: "provider",
    },
  },
  {
    field: "consultants",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "consultants",
      label: "Consultant",
      width: 320,
      height: 300,
      refField: "consultants",
      isArray: true,
    },
  },
  {
    field: "dnStatus",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "dnStatus",
      label: "DN Status",
      width: 250,
      height: 300,
      scalarField: "dnStatus",
      requiresDebitNote: true,
    },
  },
  {
    field: "dnPaymentStatus",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "dnPaymentStatus",
      label: "DN Payment Status",
      width: 250,
      height: 300,
      scalarField: "dnPaymentStatus",
      requiresDebitNote: true,
    },
  },
  {
    field: "dnPolicyStatus",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "dnPolicyStatus",
      label: "DN Policy Status",
      width: 350,
      height: 300,
      scalarField: "dnPolicyStatus",
      requiresDebitNote: true,
    },
  },
  {
    field: "orderStatus",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "orderStatus",
      label: "Order Status",
      width: 250,
      height: 300,
      scalarField: "orderStatus",
      requiresOrder: true,
    },
  },
  {
    field: "policyCommenceDate",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "policyCommenceDate",
      label: "Policy Commence Date",
      width: 250,
      height: 300,
      isPresence: true,
      requiresOrder: true,
      presenceField: "hasPolicyCommenceDate",
    },
  },
  {
    field: "policyAckSignDate",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "policyAckSignDate",
      label: "Policy Acknowledge Date",
      width: 250,
      height: 300,
      isPresence: true,
      requiresOrder: true,
      presenceField: "hasPolicyAckSignDate",
    },
  },
];

const refLabel = (doc) => {
  if (!doc) return "";
  return doc.name || doc.cname || doc.code || doc.shortName || String(doc._id);
};

const refValue = (doc) => {
  if (!doc) return null;
  return doc._id ?? doc;
};

const getScalarValue = (meta, field) => {
  const val = meta?.[field];
  if (val == null || val === "") return null;
  return val;
};

const getGroupValuesForFilter = (group, col) => {
  const meta = group.filterMeta || {};
  const filter = col.filter || {};

  if (filter.isPresence) {
    const hasValue = meta[filter.presenceField];
    return [hasValue ? PRESENCE_VALUES.SET : PRESENCE_VALUES.MISSING];
  }

  if (filter.refField === "consultants") {
    return (meta.consultants || [])
      .map((c) => refValue(c))
      .filter(Boolean);
  }

  if (filter.refField) {
    const doc = meta[filter.refField];
    const val = refValue(doc);
    return val != null ? [val] : [];
  }

  if (filter.scalarField) {
    const val = getScalarValue(meta, filter.scalarField);
    return val != null ? [val] : [];
  }

  return [];
};

export const buildLocalDistinctValues = (orderSummary, col) => {
  const filter = col.filter || {};

  if (filter.isPresence) {
    const options = [];
    const groups = (orderSummary || []).filter((group) => {
      if (filter.requiresDebitNote && !group.debitNote) return false;
      if (filter.requiresOrder && !group.order) return false;
      return true;
    });
    const hasMissing = groups.some(
      (group) => !group.filterMeta?.[filter.presenceField],
    );
    const hasSet = groups.some(
      (group) => group.filterMeta?.[filter.presenceField],
    );
    if (hasMissing) {
      options.push({ value: PRESENCE_VALUES.MISSING, label: "Missing" });
    }
    if (hasSet) {
      options.push({ value: PRESENCE_VALUES.SET, label: "Set" });
    }
    return options;
  }

  const valueMap = new Map();

  (orderSummary || []).forEach((group) => {
    if (filter.requiresDebitNote && !group.debitNote) return;
    if (filter.requiresOrder && !group.order) return;

    if (filter.refField === "consultants") {
      (group.filterMeta?.consultants || []).forEach((c) => {
        const val = refValue(c);
        if (val == null) return;
        const key = String(val);
        if (!valueMap.has(key)) {
          valueMap.set(key, { value: val, label: refLabel(c) });
        }
      });
      return;
    }

    if (filter.refField) {
      const doc = group.filterMeta?.[filter.refField];
      const val = refValue(doc);
      if (val == null) return;
      const key = String(val);
      if (!valueMap.has(key)) {
        valueMap.set(key, { value: val, label: refLabel(doc) });
      }
      return;
    }

    if (filter.scalarField) {
      const val = getScalarValue(group.filterMeta, filter.scalarField);
      if (val == null) return;
      const key = String(val);
      if (!valueMap.has(key)) {
        valueMap.set(key, { value: val, label: val });
      }
    }
  });

  return Array.from(valueMap.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label)),
  );
};

const matchesFilter = (group, field, selectedValues, col) => {
  const filter = col?.filter || {};

  if (filter.requiresDebitNote && !group.debitNote) return false;
  if (filter.requiresOrder && !group.order) return false;

  const groupValues = getGroupValuesForFilter(group, col);

  if (filter.isPresence) {
    return selectedValues.some((s) =>
      groupValues.some((v) => _.isEqual(s, v) || String(s) === String(v)),
    );
  }

  if (filter.isArray || filter.refField === "consultants") {
    if (!groupValues.length) return false;
    return groupValues.some((v) =>
      selectedValues.some((s) => _.isEqual(s, v) || String(s) === String(v)),
    );
  }

  if (!groupValues.length) return false;
  return selectedValues.some((s) =>
    groupValues.some((v) => _.isEqual(s, v) || String(s) === String(v)),
  );
};

export const applyByOrderFilters = (orderSummary, tableFilterQuery) => {
  if (!orderSummary?.length) return [];
  if (_.isEmpty(tableFilterQuery)) return orderSummary;

  const activeFilters = Object.entries(tableFilterQuery).filter(
    ([, clause]) => clause != null,
  );

  if (!activeFilters.length) return orderSummary;

  return orderSummary.filter((group) => {
    for (const [field, clause] of activeFilters) {
      const col = BY_ORDER_FILTER_COLUMNS.find((c) => c.filter?.field === field);
      if (!col) continue;

      if (clause === null) return false;

      const selectedValues = clause?.$in;
      if (!selectedValues?.length) return false;

      if (!matchesFilter(group, field, selectedValues, col)) return false;
    }
    return true;
  });
};

export const getBatchHoldAction = (filteredGroups) => {
  const actionable = (filteredGroups || []).filter((g) => (g.bidCount || 0) > 0);
  if (!actionable.length) {
    return { action: null, count: 0 };
  }

  const hasPending = actionable.some(
    (g) => (g.holdCount || 0) < (g.bidCount || 0),
  );

  if (hasPending) {
    return { action: "hold", count: actionable.length };
  }

  const allHeld = actionable.every(
    (g) => (g.holdCount || 0) >= (g.bidCount || 0),
  );
  if (allHeld) {
    return { action: "unhold", count: actionable.length };
  }

  return { action: "hold", count: actionable.length };
};

export { DN_ONLY_FIELDS, ORDER_ONLY_FIELDS };
